import React, { useState, useEffect } from "react";
import {
    Laptop,
    Zap,
    Heart,
    FileText,
    Building2,
    Handshake
} from "lucide-react"
import CustomTabs from "components/tabs/customTab";
import TicketFilter from "./TicketFilter";
import TicketCard from "./TicketCard";
import NoTickets from "./NoTickets";
import GridCard from "./CategoryGridCard";
import { Pagination } from "antd";

const SupportTabs = ({
    variant = 'it-support',
    doSearch,
    getCategory,
    getTicketStatus,
    recordList,
    filters = {},
    pagination,
    categoryData,
    ticketStatus,
}) => {
    
    const [activeTab, setActiveTab] = useState("tickets");
    
    const config = {
        'it-support': {
            ticketIcon: Laptop,
            secondTabIcon: Zap,
            secondTabLabel: "Knowledge Base",
            secondTabValue: "knowledge",
            ticketType: 'it',
        },
        'hr-support': {
            ticketIcon: Heart,
            secondTabIcon: FileText,
            secondTabLabel: "Resources",
            secondTabValue: "resources",
            ticketType: 'hr',
        },
        'partner-service': {
            ticketIcon: Handshake,
            secondTabIcon: Building2,
            secondTabLabel: "Services",
            secondTabValue: "services",
            ticketType: 'ps',
        }
    };
    
    const currentConfig = config[variant];
    
    const TabLabel = ({ icon: Icon, text }) => (
        <div className="flex items-center justify-center gap-2">
            <Icon className="text-gray-800 w-4 h-4 dark:text-gray-300" />
            <span className="text-sm font-medium">{text}</span>
        </div>
    );

    const tabOptions = [
        { 
            label: <TabLabel icon={currentConfig.ticketIcon} text={`My Tickets (${recordList?.length || 0})`} />, 
            value: "tickets" 
        },
        { 
            label: <TabLabel icon={currentConfig.secondTabIcon} text={currentConfig.secondTabLabel} />, 
            value: currentConfig.secondTabValue 
        },
    ];

    const onSelectFilter = (field, value) => {
        const data = {
            ...(filters || {}),
            page: 1,
            paginate: pagination?.per_page || 10,
            ticketType: currentConfig.ticketType,
        };

        if (value && value !== "all" && value !== 0 && value !== "") {
            if (field === "search") {
                data.keyword = value; 
            } else {
                data[field] = value;
            }
        } else {
            if (field === "search") {
                delete data.keyword;
            } else {
                delete data[field];
            }
        }
        
        doSearch(data);
    };

    const handleSearchChange = ({ keyword }) => {
        onSelectFilter("search", keyword);
    };

    useEffect(() => {
        getCategory();
        getTicketStatus();
    }, [getCategory, getTicketStatus]);

    useEffect(() => {
        doSearch({
            paginate: pagination.per_page,
            ticketType: currentConfig.ticketType
        });
    }, [doSearch, pagination.per_page, currentConfig.ticketType]);

    return (
        <div className="flex flex-col gap-2 space-y-6">
            <CustomTabs
                tabs={tabOptions}
                activeTab={activeTab}
                onChange={setActiveTab}
            />

            {activeTab === "tickets" && (
                <div className="flex-1 outline-none space-y-6">
                    <TicketFilter
                        searchQuery={filters?.search}
                        onSearchChange={handleSearchChange}
                        category={filters?.category_id || "all"}
                        onCategoryChange={(value) => onSelectFilter("category_id", value)}
                        status={filters?.status_id || "all"}
                        onStatusChange={(value) => onSelectFilter("status_id", value)}
                        categoryOptions={categoryData || []}
                        statusOptions={ticketStatus || []}
                        filters={filters}
                        pagination={pagination}
                    />
                    <div className="space-y-4">
                        {recordList && recordList.length > 0 ? (
                            [...recordList]
                                .sort((a, b) => {
                                    const statusOrder = { 'open': 0, 'in progress': 1, 'resolved': 2 };
                                    const statusDiff = (statusOrder[a?.ticket_status] ?? 3) - (statusOrder[b?.ticket_status] ?? 3);
                                    if (statusDiff !== 0) return statusDiff;
                                    
                                    const priorityOrder = { 'high': 0, 'medium': 1, 'low': 2 };
                                    return (priorityOrder[a?.ticket_priority] ?? 3) - (priorityOrder[b?.ticket_priority] ?? 3);
                                })
                                .map((ticket) => (
                                    <TicketCard 
                                        key={ticket?.tic_id}
                                        variant={variant}
                                        recordData={ticket} 
                                    />
                                ))
                        ) : (
                            <NoTickets variant={variant} />
                        )}
                        {pagination && pagination.total > 0 && (
                            <div className="flex justify-end py-4">
                                <Pagination
                                    current={pagination.current_page}
                                    pageSize={pagination.per_page}
                                    total={pagination.total}
                                    showSizeChanger={false}
                                    onChange={(page) =>
                                        doSearch({
                                            ...filters,
                                            page,
                                            paginate: pagination.per_page,
                                            ticketType: currentConfig.ticketType,
                                        })
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === currentConfig.secondTabValue && (
                <GridCard 
                    variant={variant} 
                    categoryData={categoryData}
                />
            )}
        </div>
    );
};

export default SupportTabs;